<template>
  <main class="py-10">
    <UContainer>
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Mandanten</h1>
          <p v-if="customer" class="text-gray-500 dark:text-gray-400 mt-1">
            Kunde: {{ customer.customerName }} ({{ customer.customerNo }})
          </p>
        </div>
        <div class="flex gap-2">
          <UButton
            color="neutral"
            label="Zurück"
            to="/customers"
            variant="ghost"
          />
          <UButton
            color="neutral"
            icon="i-heroicons-sparkles"
            label="Konfiguration bearbeiten"
            variant="outline"
            :loading="startingProvisioning"
            @click="startProvisioningWizard"
          />
          <UButton
            color="primary"
            label="Neuer Mandant"
            @click="openCreateModal"
          />
        </div>
      </div>

      <UCard :ui="{ body: 'p-0 sm:p-0' }">
        <UTable
          :columns="columns"
          :data="tenants"
          :loading="loading"
          class="w-full"
        >
          <template #default-cell="{ row }">
            <UBadge v-if="row.original.isDefault" color="success" variant="subtle">
              Standard
            </UBadge>
          </template>

          <template #actions-cell="{ row }">
            <div class="flex gap-2 justify-end px-4">
              <UButton
                color="neutral"
                icon="i-heroicons-cog-6-tooth"
                title="Mandantenverwaltung"
                variant="ghost"
                @click="goToAdmin(row.original.name, 'general')"
              />
              <UButton
                color="danger"
                icon="i-heroicons-trash"
                variant="ghost"
                @click="confirmDelete(row.original)"
              />
            </div>
          </template>
        </UTable>
      </UCard>
    </UContainer>
  </main>

  <!-- Create/Edit Modal -->
  <UModal
    v-model:open="isModalOpen"
    :title="state.originalName ? 'Mandant bearbeiten' : 'Neuen Mandanten anlegen'"
    description="Geben Sie hier die Details des Mandanten ein."
  >
    <template #content>
      <div class="p-6">
        <form class="space-y-4" @submit.prevent="saveTenant">
          <UFormField label="Mandantename" required>
            <UInput v-model="state.name" class="w-full" placeholder="Mandant 1" />
          </UFormField>

          <UFormField label="Primärfarbe" required>
            <USelect
              v-model="state.primaryColor"
              :items="[
                'blue',
                'green',
                'red',
                'orange',
                'yellow',
                'purple',
                'pink',
                'indigo',
                'cyan',
                'teal',
                'gray',
              ]"
              class="w-full"
            />
          </UFormField>

          <UFormField label="Standardmandant">
            <UCheckbox
              v-model="state.isDefault"
              label="Diesen Mandanten als Standard für den Kunden festlegen"
            />
          </UFormField>

          <div class="flex justify-end gap-3 pt-4">
            <UButton
              color="neutral"
              label="Abbrechen"
              variant="ghost"
              @click="isModalOpen = false"
            />
            <UButton :loading="saving" color="primary" label="Speichern" type="submit" />
          </div>
        </form>
      </div>
    </template>
  </UModal>

  <!-- Delete Confirmation Modal -->
  <UModal
    v-model:open="isDeleteModalOpen"
    description="Sind Sie sicher, dass Sie diesen Mandanten löschen möchten?"
    title="Mandant löschen"
  >
    <template #content>
      <div class="p-6 space-y-4">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Möchten Sie den Mandanten <strong>{{ tenantToDelete?.name }}</strong> wirklich löschen?
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <div class="flex justify-end gap-3 pt-2">
          <UButton
            color="neutral"
            label="Abbrechen"
            variant="ghost"
            @click="isDeleteModalOpen = false"
          />
          <UButton :loading="deleting" color="danger" label="Löschen" @click="deleteTenant" />
        </div>
      </div>
    </template>
  </UModal>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { onMounted, reactive, ref } from 'vue'
import axios from 'axios'
import type { Customer, Tenant } from '@/types'
import { useOnboarding } from '@/composables/useOnboarding'
import { backendErrorMessage } from '@/utils/api-errors'

const toast = useToast()
const router = useRouter()
const authStore = useAuthStore()

const props = defineProps({
  customerNo: {
    type: String,
    required: true,
  },
})

const tenants = ref<Tenant[]>([])
const customer = ref<Customer | null>(null)
const loading = ref(true)
const saving = ref(false)
const deleting = ref(false)
const startingProvisioning = ref(false)

const startProvisioningWizard = async () => {
  if (!props.customerNo) return
  startingProvisioning.value = true
  try {
    // One wizard covers contact, Anmeldung, Mandanten und Berechtigungen; the
    // sections the caller may edit are decided server-side.
    const { startSelfService } = useOnboarding()
    await startSelfService(props.customerNo)
    await router.push('/onboarding/setup')
  } catch (err) {
    toast.add({
      title: 'Fehler beim Starten des Assistenten',
      description: err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten',
      color: 'error',
    })
  } finally {
    startingProvisioning.value = false
  }
}

const isModalOpen = ref(false)
const isDeleteModalOpen = ref(false)
const tenantToDelete = ref<Tenant | null>(null)

const columns = [
  { accessorKey: 'name', header: 'Mandantenname' },
  { id: 'default', header: 'Standard' },
  { accessorKey: 'bucketName', header: 'Bucketname' },
  { id: 'actions', header: '' },
]

const state = reactive<{
  originalName: string | null
  name: string
  primaryColor: string
  isDefault: boolean
}>({
  originalName: null,
  name: '',
  primaryColor: 'blue',
  isDefault: false,
})

const fetchCustomer = async () => {
  try {
    const response = await axios.get(`/api/registry/v1/customers/${props.customerNo}`)
    customer.value = response.data
  } catch (error) {
    console.error('Failed to fetch customer', error)
  }
}

const fetchTenants = async () => {
  loading.value = true
  try {
    const response = await axios.get(`/api/registry/v1/customers/${props.customerNo}/tenants`)
    tenants.value = response.data
  } catch (error) {
    console.error('Failed to fetch tenants', error)
  } finally {
    loading.value = false
  }
}


const goToAdmin = async (tenantName: string, tab: string) => {
  const targetPath = `/admin/${tab}`
  const active = authStore.userInfo?.activeTenant
  if (active?.customerNo !== props.customerNo || active?.tenantName !== tenantName) {
    await authStore.requestTenantSwitch(props.customerNo, tenantName, targetPath)
  } else {
    router.push(targetPath)
  }
}

onMounted(() => {
  fetchCustomer()
  fetchTenants()
})

const openCreateModal = () => {
  state.originalName = null
  state.name = ''
  state.primaryColor = 'blue'
  state.isDefault = false
  isModalOpen.value = true
}


const saveTenant = async () => {
  saving.value = true
  try {
    if (state.originalName) {
      await axios.put(
        `/api/registry/v1/customers/${props.customerNo}/tenants/${state.originalName}`,
        {
          name: state.name,
          primaryColor: state.primaryColor,
          isDefault: state.isDefault,
          customer: customer.value,
        },
      )
    } else {
      await axios.post(`/api/registry/v1/customers/${props.customerNo}/tenants`, {
        name: state.name,
        primaryColor: state.primaryColor,
        isDefault: state.isDefault,
        customer: customer.value,
      })
    }
    isModalOpen.value = false
    await fetchTenants()
  } catch (error) {
    console.error('Failed to save tenant', error)
    toast.add({
      title: 'Fehler',
      description: backendErrorMessage(error, 'Fehler beim Speichern des Mandanten.'),
      color: 'danger',
    })
  } finally {
    saving.value = false
  }
}

const confirmDelete = (tenant: Tenant) => {
  tenantToDelete.value = tenant
  isDeleteModalOpen.value = true
}

const deleteTenant = async () => {
  if (!tenantToDelete.value) return
  deleting.value = true
  try {
    await axios.delete(
      `/api/registry/v1/customers/${props.customerNo}/tenants/${tenantToDelete.value.name}`,
    )
    isDeleteModalOpen.value = false
    await fetchTenants()
  } catch (error) {
    console.error('Failed to delete tenant', error)
    toast.add({
      title: 'Fehler',
      description: 'Fehler beim Löschen des Mandanten.',
      color: 'danger',
    })
  } finally {
    deleting.value = false
  }
}
</script>
